import { useCallback, useEffect, useRef, useState } from "react";
import {
  decodeImageFile,
  processBitmap,
} from "../lib/imageProcessing";
import type { TemplateResult, TemplateSettings } from "../lib/types";
import { DEFAULT_SETTINGS, settingsFingerprint } from "../lib/types";
import { hashFile } from "../lib/utils";
import {
  clearAllImages,
  deleteImage as deleteImageFromDb,
  listImages,
  persistImage,
  persistImageSettings,
} from "../lib/storage";

export interface ImageState {
  id: string;
  fileName: string;
  fileSize: number;
  imageHash: string;
  bitmap: ImageBitmap;
  settings: TemplateSettings;
  result: TemplateResult | null;
  error: string | null;
  processing: boolean;
  progress: Set<number>;
}

const PROGRESS_KEY_PREFIX = "crochet:progress:";
const DEFAULTS_KEY = "crochet:defaults";

function progressKey(hash: string, s: TemplateSettings) {
  return `${PROGRESS_KEY_PREFIX}${hash}:${settingsFingerprint(s)}`;
}

function loadProgress(hash: string, s: TemplateSettings): Set<number> {
  try {
    const raw = localStorage.getItem(progressKey(hash, s));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as number[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

function saveProgress(hash: string, s: TemplateSettings, prog: Set<number>): void {
  try {
    localStorage.setItem(progressKey(hash, s), JSON.stringify(Array.from(prog)));
  } catch {
    // ignore quota errors
  }
}

function loadDefaults(): TemplateSettings {
  try {
    const raw = localStorage.getItem(DEFAULTS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<TemplateSettings>) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function saveDefaults(s: TemplateSettings): void {
  try {
    localStorage.setItem(DEFAULTS_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

/** Tiny concurrency-limited runner. */
class Limiter {
  private active = 0;
  private queue: Array<() => void> = [];
  constructor(private max: number) {}
  async run<T>(task: () => Promise<T>): Promise<T> {
    if (this.active >= this.max) {
      await new Promise<void>((resolve) => this.queue.push(resolve));
    }
    this.active++;
    try {
      return await task();
    } finally {
      this.active--;
      const next = this.queue.shift();
      if (next) next();
    }
  }
}

let nextId = 1;
const limiter = new Limiter(2);

function newId(): string {
  return `img-${Date.now()}-${nextId++}`;
}

export function useImages() {
  const [images, setImages] = useState<ImageState[]>([]);
  const [defaults, setDefaultsState] = useState<TemplateSettings>(() => loadDefaults());
  const [hydrated, setHydrated] = useState(false);
  // Track which (id+settings fingerprint) jobs we've already kicked off, to avoid loops.
  const jobToken = useRef(new Map<string, string>());

  // Hydrate persisted images from IndexedDB on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const persisted = await listImages();
        if (cancelled || persisted.length === 0) {
          setHydrated(true);
          return;
        }
        const restored: ImageState[] = [];
        for (const rec of persisted) {
          try {
            // Reconstruct a File so the existing decode path (which respects EXIF) just works.
            const file = new File([rec.bytes], rec.fileName, { type: rec.fileType });
            const bitmap = await decodeImageFile(file);
            const progress = loadProgress(rec.imageHash, rec.settings);
            restored.push({
              id: rec.id,
              fileName: rec.fileName,
              fileSize: rec.bytes.byteLength,
              imageHash: rec.imageHash,
              bitmap,
              settings: rec.settings,
              result: null,
              error: null,
              processing: true,
              progress,
            });
          } catch (err) {
            console.warn("Failed to hydrate persisted image", rec.fileName, err);
          }
        }
        if (!cancelled) {
          setImages(restored);
          setHydrated(true);
        }
      } catch (err) {
        console.warn("Failed to list persisted images", err);
        setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setDefaults = useCallback((s: TemplateSettings) => {
    setDefaultsState(s);
    saveDefaults(s);
  }, []);

  const addFiles = useCallback(async (files: File[]) => {
    const accepted = files.filter((f) => f.type.startsWith("image/"));
    for (const file of accepted) {
      try {
        const bytes = await file.arrayBuffer();
        const [hash, bitmap] = await Promise.all([hashFile(file), decodeImageFile(file)]);
        const id = newId();
        const settings = { ...defaults };
        const progress = loadProgress(hash, settings);
        const createdAt = Date.now();

        // Persist the original bytes immediately so a refresh during processing still recovers.
        await persistImage({
          id,
          fileName: file.name,
          fileType: file.type || "image/jpeg",
          imageHash: hash,
          settings,
          createdAt,
          bytes,
        });

        setImages((prev) => [
          ...prev,
          {
            id,
            fileName: file.name,
            fileSize: file.size,
            imageHash: hash,
            bitmap,
            settings,
            result: null,
            error: null,
            processing: true,
            progress,
          },
        ]);
      } catch (err) {
        console.error("Failed to load file", file.name, err);
      }
    }
  }, [defaults]);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target) {
        try { target.bitmap.close(); } catch { /* noop */ }
      }
      return prev.filter((i) => i.id !== id);
    });
    deleteImageFromDb(id).catch((err) => console.warn("Failed to remove from DB", err));
  }, []);

  const removeAll = useCallback(() => {
    setImages((prev) => {
      for (const img of prev) {
        try { img.bitmap.close(); } catch { /* noop */ }
      }
      return [];
    });
    clearAllImages().catch((err) => console.warn("Failed to clear DB", err));
  }, []);

  const updateSettings = useCallback((id: string, patch: Partial<TemplateSettings> | TemplateSettings) => {
    setImages((prev) =>
      prev.map((img) => {
        if (img.id !== id) return img;
        const newSettings: TemplateSettings = { ...img.settings, ...patch };
        const newProgress = loadProgress(img.imageHash, newSettings);
        // Persist new settings asynchronously so a refresh resumes with them.
        persistImageSettings(id, newSettings).catch((err) =>
          console.warn("Failed to persist settings", err)
        );
        return { ...img, settings: newSettings, processing: true, progress: newProgress };
      })
    );
  }, []);

  const toggleCell = useCallback((id: string, cellIdx: number) => {
    setImages((prev) =>
      prev.map((img) => {
        if (img.id !== id) return img;
        const next = new Set(img.progress);
        if (next.has(cellIdx)) next.delete(cellIdx);
        else next.add(cellIdx);
        saveProgress(img.imageHash, img.settings, next);
        return { ...img, progress: next };
      })
    );
  }, []);

  const resetProgress = useCallback((id: string) => {
    setImages((prev) =>
      prev.map((img) => {
        if (img.id !== id) return img;
        const empty = new Set<number>();
        saveProgress(img.imageHash, img.settings, empty);
        return { ...img, progress: empty };
      })
    );
  }, []);

  // Whenever an image is missing its result (or settings changed), recompute via the limiter.
  useEffect(() => {
    const stale = images.filter((img) => img.processing);
    if (stale.length === 0) return;
    let cancelled = false;
    for (const img of stale) {
      const fp = settingsFingerprint(img.settings);
      const prevToken = jobToken.current.get(img.id);
      if (prevToken === fp) continue;
      jobToken.current.set(img.id, fp);
      limiter
        .run(async () => processBitmap(img.bitmap, img.settings))
        .then((result) => {
          if (cancelled) return;
          setImages((prev) =>
            prev.map((it) =>
              it.id === img.id
                ? { ...it, result, processing: false, error: null }
                : it
            )
          );
        })
        .catch((err) => {
          if (cancelled) return;
          setImages((prev) =>
            prev.map((it) =>
              it.id === img.id
                ? { ...it, processing: false, error: String(err?.message ?? err) }
                : it
            )
          );
        });
    }
    return () => {
      cancelled = true;
    };
  }, [images]);

  return {
    images,
    defaults,
    hydrated,
    setDefaults,
    addFiles,
    removeImage,
    removeAll,
    updateSettings,
    toggleCell,
    resetProgress,
  };
}
